import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import {
  IAutoQuote,
  IAutoCheckout,
  IAutoThirdPartyDataOrders,
  IDriver,
  IVehicle,
  IAutoEnrollmentApplication,
  IOktaAccessToken,
  IOktaIdToken,
  IOktaLoginInfo,
  IUser,
  IRentersPolicy,
  IRentersQuote,
  IRentersQuoteDetails,
  IRentersQuoteApplicationVariables,
  IRentersCheckout,
  IRentersPurchaseInfo,
  IRentersQuotePlan,
  IQuoteUUID,
  IAutoVariables,
  IAutoVariablesPayload,
  IRentersVariablesPayload,
  IAutoPolicy,
  TPolicy,
  TPolicyHealthCheckErrorState,
} from '@sureapp/platform-sdk';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment-timezone';
import { ParsedQs } from 'qs';

import {
  ICmsContent,
  ICmsError,
  IFeatureFlag,
  IQuoteData,
  IQuoteHealthCheckState,
} from '@@/auto/interfaces';

import { TAddEditVehicleFormValidationSchema } from '../components/AddEditVehicleForm/AddEditVehicleFormValidationSchema.types';

import * as actionType from './actionCreators';

const Reducers = {
  authToken: createReducer<string | null>(null, (builder) =>
    builder
      .addCase(
        actionType.exchangeRentersQuoteAuthTokenSuccess,
        (_, action) => action.payload
      )
      .addCase(actionType.exchangeRentersQuoteAuthTokenFailure, () => null)
      .addCase(
        actionType.exchangeAutoQuoteAuthTokenSuccess,
        (_, action) => action.payload
      )
      .addCase(actionType.exchangeAutoQuoteAuthTokenFailure, () => null)
      .addCase(actionType.clearOktaForQA, () => null)
      .addDefaultCase((state) => state)
  ),

  oktaIdToken: createReducer<IOktaIdToken | null>(null, (builder) =>
    builder
      .addCase(actionType.saveOktaIdToken, (_, action) => action.payload.token)
      .addCase(
        actionType.refreshOktaTokensSuccess,
        (_, action) => action.payload.idToken
      )
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addCase(actionType.clearOktaForQA, () => null)
      .addDefaultCase((state) => state)
  ),

  oktaAccessToken: createReducer<IOktaAccessToken | null>(null, (builder) => {
    builder
      .addCase(actionType.saveOktaAccessToken, (_, action) => action.payload)
      .addCase(
        actionType.refreshOktaTokensSuccess,
        (_, action) => action.payload.accessToken
      )
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addCase(actionType.clearOktaForQA, () => null)
      .addDefaultCase((state) => state);
  }),

  oktaLoginInfo: createReducer<IOktaLoginInfo | null>(null, (builder) => {
    builder
      .addCase(actionType.createOktaLoginInfo, (_, action) => action.payload)
      .addCase(actionType.clearOktaLoginInfo, () => null)
      .addCase(actionType.clearOktaForQA, () => null)
      .addDefaultCase((state) => state);
  }),

  isCCPABannerOpen: createReducer<boolean>(true, (builder) => {
    builder
      .addCase(actionType.closeCCPABanner, () => false)
      .addDefaultCase((state) => state);
  }),

  user: createReducer<IUser | null>(null, (builder) => {
    builder
      .addCase(actionType.getUserSuccess, (_, action) => action.payload)
      .addCase(actionType.updateUserSuccess, (_, action) => action.payload)
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  plans: createReducer<IRentersQuotePlan[]>([], (builder) => {
    builder
      .addCase(actionType.getPlansSuccess, (_, action) => action.payload.plans)
      .addCase(actionType.finalizeQuoteSuccess, () => [])
      .addCase(actionType.saveOktaIdToken, () => [])
      .addCase(actionType.logout, () => [])
      .addCase(actionType.refreshOktaTokensFailure, () => [])
      .addDefaultCase((state) => state);
  }),

  enrollmentApplication: createReducer<IAutoEnrollmentApplication | null>(
    null,
    (builder) => {
      builder
        .addCase(
          actionType.updateEnrollmentApplication,
          (_, action) => action.payload
        )
        .addCase(actionType.getAutoQuoteSuccess, (_, action) => {
          const application = action?.payload?.application;
          return {
            firstName: application?.client?.first_name || '',
            lastName: application?.client?.last_name || '',
            dob: application?.client?.dob || '',
            address: application?.rating_address || {},
            selfReportedIncidentCount: application.self_reported_incident_count,
            coverageEffectiveDate: application?.policy_effective_date || '',
          };
        })
        .addCase(actionType.clearEnrollmentApplication, () => null)
        .addCase(actionType.logout, () => null)
        .addCase(actionType.refreshOktaTokensFailure, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quote: createReducer<IRentersQuote | IAutoQuote | null>(null, (builder) => {
    builder
      .addCase(actionType.getAutoQuoteSuccess, (_, action) => action.payload)
      .addCase(actionType.createAutoQuoteSuccess, (_, action) => action.payload)
      .addCase(actionType.updateAutoQuoteSuccess, (_, action) => action.payload)
      .addCase(
        actionType.createQuoteSuccess,
        (_, action) => action.payload.quote
      )
      .addCase(
        actionType.updateQuoteSuccess,
        (_, action) => action.payload.quote
      )
      .addCase(actionType.clearAutoQuote, () => null)
      .addCase(actionType.finalizeQuoteSuccess, () => null)
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  quoteData: createReducer<IQuoteData>(
    {
      isMakeChangesFlow: false,
      hasSeenAdjustedPriceModal: false,
      excluded_vehicles: [],
    },
    (builder) => {
      builder
        .addCase(actionType.clearAutoQuote, () => ({
          isMakeChangesFlow: false,
          hasSeenAdjustedPriceModal: false,
          excluded_vehicles: [],
        }))
        .addCase(actionType.updateQuoteData, (state, action) => ({
          ...state,
          ...action.payload,
        }))
        .addCase(actionType.exchangeAutoQuoteAuthTokenSuccess, () => ({
          isMakeChangesFlow: false,
          hasSeenAdjustedPriceModal: false,
          excluded_vehicles: [],
        }))
        .addCase(actionType.exchangeAutoQuoteAuthTokenFailure, () => ({
          isMakeChangesFlow: false,
          hasSeenAdjustedPriceModal: false,
          excluded_vehicles: [],
        }));
    }
  ),

  quoteVars: createReducer<Partial<IRentersVariablesPayload> | IAutoVariables>(
    {},
    (builder) => {
      builder
        .addCase(actionType.getQuoteVarsSuccess, (_, action) => action.payload)
        .addCase(
          actionType.getAutoQuoteVarsSuccess,
          (_, action) => action.payload.variables
        )
        .addCase(actionType.createQuoteSuccess, () => ({}))
        .addCase(actionType.finalizeQuoteSuccess, () => ({}))
        .addCase(actionType.finalizeAutoQuoteSuccess, () => ({}))
        .addCase(actionType.clearAutoQuote, () => ({}))
        .addCase(actionType.saveOktaIdToken, (state, action) =>
          action.payload.hasLoggedInDuringPurchaseFlow ? state : {}
        )
        .addCase(actionType.logout, () => ({}))
        .addCase(actionType.refreshOktaTokensFailure, () => ({}))
        .addCase(actionType.exchangeRentersQuoteAuthTokenSuccess, () => ({}))
        .addCase(actionType.exchangeRentersQuoteAuthTokenFailure, () => ({}))
        .addCase(actionType.exchangeAutoQuoteAuthTokenSuccess, () => ({}))
        .addCase(actionType.exchangeAutoQuoteAuthTokenFailure, () => ({}))
        .addDefaultCase((state) => state);
    }
  ),

  quoteExtraData: createReducer<{ [key: string]: any } | null>(
    null,
    (builder) => {
      builder
        .addCase(
          actionType.getAutoQuoteVarsSuccess,
          (_, action) => action.payload.extra_data
        )
        .addCase(actionType.clearAutoQuote, () => null)
        .addCase(actionType.logout, () => null)
        .addCase(actionType.refreshOktaTokensFailure, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quoteDetails: createReducer<Partial<IRentersQuoteDetails>>({}, (builder) => {
    builder
      .addCase(actionType.updateQuoteDetails, (state, action) => ({
        ...state,
        ...action.payload,
      }))
      .addCase(actionType.exchangeRentersQuoteAuthTokenSuccess, () => ({}))
      .addCase(actionType.exchangeAutoQuoteAuthTokenSuccess, () => ({}))
      .addCase(actionType.finalizeQuoteSuccess, () => ({}))
      .addCase(actionType.saveOktaIdToken, () => ({}))
      .addCase(actionType.logout, () => ({}))
      .addCase(actionType.refreshOktaTokensFailure, () => ({}))
      .addDefaultCase((state) => state);
  }),

  variableSelections: createReducer<Partial<IRentersQuoteApplicationVariables>>(
    {},
    (builder) => {
      builder
        .addCase(
          actionType.updateVariableSelections,
          (
            state,
            action: PayloadAction<Partial<IRentersQuoteApplicationVariables>>
          ) => ({
            ...state,
            ...action.payload,
          })
        )
        .addCase(actionType.updateQuoteSuccess, (state, action) => ({
          ...state,
          ...action.payload.quote.application.variables,
        }))
        .addCase(actionType.finalizeQuoteSuccess, () => ({}))
        .addCase(actionType.saveOktaIdToken, () => ({}))
        .addCase(actionType.logout, () => ({}))
        .addCase(actionType.refreshOktaTokensFailure, () => ({}))
        .addDefaultCase((state) => state);
    }
  ),

  quoteCheckout: createReducer<
    Partial<IRentersCheckout | IAutoCheckout | null>
  >({}, (builder) => {
    builder
      .addCase(actionType.checkoutQuoteSuccess, (_, action) => action.payload)
      .addCase(
        actionType.getAutoQuoteCheckoutSuccess,
        (_, action) => action.payload
      )
      .addCase(actionType.clearAutoQuote, () => null)
      .addCase(actionType.finalizeQuoteSuccess, () => null)
      .addCase(actionType.finalizeAutoQuoteSuccess, () => null)
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addCase(actionType.exchangeRentersQuoteAuthTokenSuccess, () => null)
      .addCase(actionType.exchangeRentersQuoteAuthTokenFailure, () => null)
      .addCase(actionType.exchangeAutoQuoteAuthTokenSuccess, () => null)
      .addCase(actionType.exchangeAutoQuoteAuthTokenFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  quoteHealthCheckCachedDrivers: createReducer<IDriver[] | null>(
    null,
    (builder) => {
      builder
        .addCase(
          actionType.updateCachedQuoteHealthCheckState,
          (state, action) => action.payload.drivers || state
        )
        .addCase(actionType.createAutoQuoteSuccess, () => null)
        .addCase(actionType.clearAutoQuote, () => null)
        .addCase(actionType.finalizeAutoQuoteSuccess, () => null)
        .addCase(actionType.getPoliciesRequest, () => null)
        .addCase(actionType.logout, () => null)
        .addCase(actionType.refreshOktaTokensFailure, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  policyHealthCheckErrorDetails: createReducer<TPolicyHealthCheckErrorState | null>(
    null,
    (builder) => {
      builder
        .addCase(
          actionType.updatePolicyHealthCheckErrorDetails,
          (state, action: PayloadAction<TPolicyHealthCheckErrorState>) =>
            action.payload || state
        )
        .addCase(actionType.clearPolicyHealthCheckErrorDetails, () => null)
        .addCase(actionType.clearStagedAutoPolicyRequest, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quoteHealthCheckCachedVehicles: createReducer<IVehicle[] | null>(
    null,
    (builder) => {
      builder
        .addCase(
          actionType.updateCachedQuoteHealthCheckState,
          (state, action) => action.payload.vehicles || state
        )
        .addCase(actionType.createAutoQuoteSuccess, () => null)
        .addCase(actionType.clearAutoQuote, () => null)
        .addCase(actionType.finalizeAutoQuoteSuccess, () => null)
        .addCase(actionType.getPoliciesRequest, () => null)
        .addCase(actionType.logout, () => null)
        .addCase(actionType.refreshOktaTokensFailure, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  quoteHealthCheckCachedOrders: createReducer<
    IAutoThirdPartyDataOrders[] | null
  >(null, (builder) => {
    builder
      .addCase(
        actionType.updateCachedQuoteHealthCheckState,
        (state, action: PayloadAction<IQuoteHealthCheckState>) =>
          action.payload.orders || state
      )
      .addCase(actionType.createAutoQuoteSuccess, () => null)
      .addCase(actionType.clearAutoQuote, () => null)
      .addCase(actionType.finalizeAutoQuoteSuccess, () => null)
      .addCase(actionType.getPoliciesRequest, () => null)
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  purchaseInfo: createReducer<Partial<IRentersPurchaseInfo>>({}, (builder) => {
    builder
      .addCase(actionType.checkoutQuoteSuccess, (state, action) => ({
        ...state,
        quoteCheckout: action.payload,
      }))
      .addCase(
        actionType.finalizeQuoteSuccess,
        (state, action: PayloadAction<{ quote: IRentersQuote }>) => ({
          ...state,
          quote: action.payload.quote || state.quote,
          purchasedAt: moment()
            .tz(action.payload.quote.application.rating_address.timezone ?? '')
            .toISOString(),
        })
      )
      .addCase(actionType.logout, () => ({}))
      .addCase(actionType.refreshOktaTokensFailure, () => ({}))
      .addDefaultCase((state) => state);
  }),

  policies: createReducer<TPolicy[]>([], (builder) => {
    builder
      .addCase(
        actionType.getPoliciesSuccess,
        (_, action) => action.payload.policies
      )
      .addCase(actionType.updatePolicySuccess, (state, action) =>
        sortBy(
          uniqBy([action.payload.policy, ...state], 'id'),
          (policy) => -moment(policy.ends_at).valueOf()
        )
      )
      .addCase(
        actionType.cancelPolicySuccess,
        (state, action: PayloadAction<{ policy: TPolicy }>) =>
          sortBy(
            uniqBy([action.payload.policy, ...state], 'id'),
            (policy) => -moment(policy.ends_at).valueOf()
          )
      )
      .addCase(actionType.createAutoQuoteSuccess, () => [])
      .addCase(actionType.logout, () => [])
      .addCase(actionType.refreshOktaTokensFailure, () => [])
      .addDefaultCase((state) => state);
  }),

  policy: createReducer<TPolicy | null>(null, (builder) => {
    builder
      .addCase(
        actionType.getPolicySuccess,
        (_, action) => action.payload.policy
      )
      .addCase(
        actionType.updatePolicySuccess,
        (_, action) => action.payload.policy
      )
      .addCase(
        actionType.cancelPolicySuccess,
        (_, action) => action.payload.policy
      )
      .addCase(actionType.createAutoQuoteSuccess, () => null)
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  policyVars: createReducer<
    Partial<IRentersVariablesPayload | IAutoVariablesPayload>
  >({}, (builder) => {
    builder
      .addCase(
        actionType.getPolicyVarsSuccess,
        (
          _,
          action: PayloadAction<
            Partial<IRentersVariablesPayload | IAutoVariablesPayload>
          >
        ) => action.payload
      )
      .addCase(actionType.createAutoQuoteSuccess, () => ({}))
      .addCase(actionType.logout, () => ({}))
      .addCase(actionType.refreshOktaTokensFailure, () => ({}))
      .addDefaultCase((state) => state);
  }),

  policyCheckout: createReducer<IRentersCheckout | IAutoCheckout | null>(
    null,
    (builder) => {
      builder
        .addCase(
          actionType.getPolicyCheckoutSuccess,
          (state, action: PayloadAction<IRentersCheckout | IAutoCheckout>) =>
            action.payload
        )
        .addCase(actionType.createAutoQuoteSuccess, () => null)
        .addCase(actionType.logout, () => null)
        .addCase(actionType.refreshOktaTokensFailure, () => null)
        .addCase(actionType.clearStagedAutoPolicyRequest, () => null)
        .addDefaultCase((state) => state);
    }
  ),

  editingPolicy: createReducer<IRentersPolicy | null>(null, (builder) => {
    builder
      .addCase(
        actionType.updateEditingPolicy,
        (_, action: PayloadAction<{ policy: IRentersPolicy }>) =>
          action.payload.policy
      )
      .addCase(actionType.clearEditingPolicy, () => null)
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  stagedAutoPolicy: createReducer<IAutoPolicy | null>(null, (builder) => {
    builder
      .addCase(
        actionType.updateStagedAutoPolicyRequest,
        (_, action) => action.payload.policy
      )
      .addCase(actionType.getPolicySuccess, (state, action) =>
        state ? { ...action.payload.policy, ...state } : null
      )
      .addCase(actionType.clearStagedAutoPolicyRequest, () => null)
      .addCase(actionType.logout, () => null)
      .addCase(actionType.refreshOktaTokensFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  urlParams: createReducer<ParsedQs>({}, (builder) => {
    builder.addDefaultCase((state) => state);
  }),

  uuid: createReducer<IQuoteUUID | null>(null, (builder) => {
    builder
      .addCase(
        actionType.finalizeQuoteRequest,
        (_, action: PayloadAction<{ uuid: IQuoteUUID }>) => action.payload.uuid
      )
      .addCase(actionType.finalizeQuoteSuccess, () => null)
      .addCase(actionType.finalizeQuoteFailure, () => null)
      .addDefaultCase((state) => state);
  }),

  featureFlags: createReducer<IFeatureFlag[]>([], (builder) => {
    builder
      .addCase(
        actionType.getFeatureFlagsSuccess,
        (_, action: PayloadAction<IFeatureFlag[]>) => action.payload
      )
      .addDefaultCase((state) => state);
  }),

  cmsContent: createReducer<Record<string, ICmsContent | ICmsError>>(
    {},
    (builder) => {
      builder
        .addCase(actionType.getCmsContentSuccess, (state, action) => ({
          ...state,
          [action.payload.slug]: action.payload,
        }))
        .addCase(actionType.getCmsContentFailure, (state, action) => ({
          ...state,
          [action.payload.slug]: action.payload,
        }));
    }
  ),

  hasLoggedInDuringPurchaseFlow: createReducer<boolean>(false, (builder) => {
    builder
      .addCase(
        actionType.updateHasLoggedInDuringPurchaseFlow,
        (_, action) => action.payload
      )
      .addCase(actionType.createAutoQuoteSuccess, () => false)
      .addCase(actionType.clearAutoQuote, () => false)
      .addCase(actionType.logout, () => false)
      .addCase(actionType.refreshOktaTokensFailure, () => false)
      .addDefaultCase((state) => state);
  }),

  isAgentUsingQuoteAssist: createReducer<boolean>(false, (builder) => {
    builder
      .addCase(
        actionType.updateIsAgentUsingQuoteAssist,
        (_, action) => action.payload
      )
      .addCase(actionType.clearAutoQuote, () => false)
      .addCase(actionType.clearEnrollmentApplication, () => false)
      .addDefaultCase((state) => state);
  }),

  stashedAddEditVehicleFormValues: createReducer<TAddEditVehicleFormValidationSchema | null>(
    null,
    (builder) => {
      builder
        .addCase(
          actionType.updateStashedAddEditVehicleFormValues,
          (_, action) => action.payload
        )
        .addCase(actionType.clearStashedAddEditVehicleFormValues, () => null)
        .addCase(actionType.clearAutoQuote, () => null)
        .addCase(actionType.clearStagedAutoPolicyRequest, () => null)
        .addDefaultCase((state) => state);
    }
  ),
};

export default Reducers;
